import { faCalculator, faEdit } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, Divider, Form, Input, Row, Select, Space, Statistic, Switch, Typography } from "antd"
import { useContext, useEffect, useReducer, useState } from "react"
import { AuthenticationContext } from "../../AuthenticationContext"
import { ItemClasses, itemClassName } from "../../common/constants"
import { CatalogItem } from "../../domain/catalogItem"
import { ProductionRate } from "../../domain/productionRate"
import { hasValue } from "../../helpers/nullable"
import { formatPercentage } from "../BudgetCalculator/budgetCalculatorModel"
import { useBudgets } from "../Budgets/budgetQueries"
import PercentInput from "../Budgets/PercentInput"
import DollarInput from "../common/DollarInput"
import { useItemCategories, useItemTags } from "./apiQueries"
import { reducer, updateProductionRate, updateName, updateSku, updateClass, updateUnits, updateCost, updateTaxable, updateOvertimeFactor, updateUnbillablePercent, updateLaborBurden, updateCustomPrice, updateCustomProfitMargin, updateDescription, updateCategories, updateTags, Action, resetForm } from "./catalogItemFormReducer"
import { CatalogItemFormValues, catalogItemToFormValues, denormalizePercentages, normalizePercentages } from "./catalogItemModel"
import PricingBreakdown from "./PricingBreakdown"
import ProductionRateForm from "./ProductionRateForm"

const { Text } = Typography

export type Props = {
  catalogItem: CatalogItem
  onChanges: (values: CatalogItemFormValues, dirty: boolean) => void
}

const CatalogItemForm = ({ catalogItem, onChanges }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const itemCategories = useItemCategories(true)
  const itemTags = useItemTags(true)
  const budgetsQuery = useBudgets()
  useEffect(() => {
    if (budgetsQuery.data) {
      setBudgetYear(budgetsQuery.data.defaultYear)
    }
  }, [budgetsQuery.data])

  const initialValues: CatalogItemFormValues = catalogItemToFormValues(normalizePercentages(catalogItem))
  const [state, originalDispatch] = useReducer(reducer, initialValues)
  const dispatch = (action: Action) => {
    originalDispatch(action)
    onChanges(denormalizePercentages(reducer(state, action)), true)
  }

  useEffect(() => {
    resetForm(dispatch)(initialValues)
    onChanges(denormalizePercentages(initialValues), false)
  }, [catalogItem])

  const classOptions = [
    { label: 'Labor', value: ItemClasses.Labor },
    { label: 'Equipment', value: ItemClasses.Equipment },
    { label: 'Materials', value: ItemClasses.Materials },
    { label: 'Subcontracting', value: ItemClasses.Subcontracting },
  ]

  const [showProductionRateForm, setShowProductionRateForm] = useState(false)
  const hasProductionRate = hasValue(state.rateInstallationQuantity) && hasValue(state.ratePerUnits) && hasValue(state.rateUsageQuantity)
  const productionRate: ProductionRate = {
    rateInstallationQuantity: state.rateInstallationQuantity ?? 0,
    ratePerUnits: state.ratePerUnits ?? '',
    rateUsageQuantity: state.rateUsageQuantity ?? 0,
    units: state.units ?? ''
  }

  const categoryOptions = itemCategories.data?.map(x => ({ label: x.name, value: x.name }))
  const tagOptions = itemTags.data?.map(x => ({ label: x, value: x }))

  const [budgetYear, setBudgetYear] = useState<number | undefined>()
  const classBudget = budgetsQuery.data?.budgets.find(x => x.year === budgetYear && x.class === state.itemClass)
  const budgetLabel = (title: string) =>
    <Space>
      {title}
      <small>
        <Text type="secondary">
          {budgetYear} {itemClassName(state.itemClass)} budget
        </Text>
      </small>
    </Space>

  const formatDate = (date?: string) => date ? new Date(Date.parse(date)).toLocaleDateString() : ''

  const handleProductionRateChange = (productionRate?: ProductionRate) => {
    updateProductionRate(dispatch)(productionRate)
    setShowProductionRateForm(false)
  }

  return <>
    <Form layout="vertical">
      <Form.Item name="id" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="guid" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="saveToCatalog" hidden><Input type="hidden" /></Form.Item>
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item label="Name">
            <Input value={state.name} onChange={e => updateName(dispatch)(e.target.value)} maxLength={150} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="SKU">
            <Input value={state.sku} onChange={e => updateSku(dispatch)(e.target.value)} maxLength={25} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Class">
            <Select options={classOptions} value={state.itemClass} onChange={updateClass(dispatch)} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item label="Production Rate">
            {hasProductionRate
              ? <Space.Compact>
                <Input
                  value={`${state.rateInstallationQuantity} ${state.ratePerUnits} per ${state.rateUsageQuantity} ${state.units}`}
                  readOnly
                  onClick={() => setShowProductionRateForm(true)}
                  style={{ cursor: 'pointer' }}
                  maxLength={50}
                />
                <Button icon={<FontAwesomeIcon icon={faEdit} onClick={() => setShowProductionRateForm(true)} />} />
              </Space.Compact>
              : <Button
                icon={<FontAwesomeIcon icon={faCalculator} />}
                onClick={() => setShowProductionRateForm(true)}
              >
                Set Production Rate
              </Button>
            }
          </Form.Item>
          <ProductionRateForm
            productionRate={productionRate}
            isOpen={showProductionRateForm}
            onChange={handleProductionRateChange}
            onCancel={() => setShowProductionRateForm(false)}
            onClear={() => handleProductionRateChange(undefined)}
          />
        </Col>
        <Col span={8}>
          <Form.Item label="Units">
            <Input value={state.units} onChange={e => updateUnits(dispatch)(e.target.value)} />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Row gutter={24}>
        <Col span={12}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={state.itemClass === ItemClasses.Labor ? "Average Wage" : "Cost"}>
                <DollarInput
                  style={{ width: '100%' }}
                  addonAfter={state.itemClass === ItemClasses.Labor ? 'per person' : undefined}
                  value={state.cost}
                  onChange={value => updateCost(dispatch)(value ?? 0)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Taxable">
                <Switch value={state.taxable} onChange={updateTaxable(dispatch)} />
              </Form.Item>
            </Col>
          </Row>
          {state.itemClass === ItemClasses.Labor && currentUser?.tenant.enableLaborAdjustedCost && <>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="Overtime Factor">
                  <PercentInput
                    value={state.overtimeFactor}
                    onChange={updateOvertimeFactor(dispatch)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="Unbillable">
                  <PercentInput
                    value={state.unbillablePercent}
                    onChange={updateUnbillablePercent(dispatch)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Labor Burden">
                  <PercentInput
                    value={state.laborBurden}
                    onChange={updateLaborBurden(dispatch)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={budgetLabel("Overhead")}>
                <Statistic value={formatPercentage((classBudget?.overhead ?? 0) / 100)} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={budgetLabel("Profit Margin")}>
                <Statistic value={formatPercentage((classBudget?.profit ?? 0) / 100)} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Custom Price">
                <DollarInput
                  value={state.customPrice ?? undefined}
                  onChange={updateCustomPrice(dispatch)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Custom Profit Margin">
                <PercentInput
                  value={state.customProfitMargin}
                  onChange={updateCustomProfitMargin(dispatch)}
                  allowClear
                  style={{ width: '100%' }}
                  max={0.9999}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <PricingBreakdown
            catalogItem={state}
            budgetYear={budgetYear ?? budgetsQuery.data?.defaultYear ?? 0}
            onBudgetYearChange={setBudgetYear}
          />
        </Col>
      </Row>
      <Divider />
      <Form.Item label="Description">
        <Input.TextArea
          value={state.description}
          onChange={e => updateDescription(dispatch)(e.target.value)}
        />
      </Form.Item>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label="Categories">
            <Select
              value={state.categories}
              onChange={updateCategories(dispatch)}
              mode="multiple"
              options={categoryOptions}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Tags">
            <Select
              value={state.tags}
              onChange={updateTags(dispatch)}
              mode="tags"
              options={tagOptions}
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item label="Created">
            {formatDate(catalogItem.addedAt)}
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Price last updated">
            {formatDate(catalogItem.priceLastUpdatedAt)}
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Last used">
            {formatDate(catalogItem.lastUsedAt)}
          </Form.Item>
        </Col>
      </Row>
    </Form>

  </>
}

export default CatalogItemForm
